import { useEffect, useMemo, useState } from "react";
import styles from "./CompanySettings.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import AuditLogModal from "../../../components/System&Settings/CompanySettings/AuditLogModal";
import ConfirmSaveModal from "../../../components/System&Settings/CompanySettings/ConfirmSaveModal";
import { useToast } from "../../../context/ToastContext";
import {
  useGetCompanySettings,
  useUpdateCompanySettings,
} from "../../../api/sytem&settings/useCompanySettings";

const TABS = [
  { id: "legal",     label: "Legal & Address",   icon: "🏢" },
  { id: "documents", label: "Document Display",   icon: "📄" },
  { id: "currency",  label: "Currency Format",    icon: "💶" },
  { id: "audit",     label: "Validity & Audit",   icon: "📋" },
];

const DEFAULT_FORM = {
  legalName:          "SUNSKY Travel NV",
  tradeName:          "SUNSKY",
  legalForm:          "NV",
  registrationNumber: "0123.456.789",
  vatNumber:          "BE0123456789",
  vatCountry:         "BE",
  licenseNumber:      "LIC-2024-00123",
  street:             "Antwerpsesteenweg",
  houseNumber:        "45",
  postalCode:         "2000",
  city:               "Antwerp",
  country:            "BE",
  email:              "info@sunsky.be",
  phone:              "+32 3 123 45 67",
  displayNameMode:    "trade",
  showAddress:        true,
  showVat:            true,
  showLicense:        false,
  effectiveFrom:      "2024-01-15",
};

const COUNTRY_CODE_TO_NAME = {
  BE: "Belgium",
  NL: "Netherlands",
  DE: "Germany",
  FR: "France",
  LU: "Luxembourg",
};

const COUNTRY_NAME_TO_CODE = Object.fromEntries(
  Object.entries(COUNTRY_CODE_TO_NAME).map(([code, name]) => [name.toLowerCase(), code])
);

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeCountryCode = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const upper = raw.toUpperCase();
  if (COUNTRY_CODE_TO_NAME[upper]) return upper;

  return COUNTRY_NAME_TO_CODE[raw.toLowerCase()] || "";
};

const getDocumentNameMode = (rows = []) => {
  const active = rows.find((row) => row?.status === true) || rows[0];
  const name = String(active?.name || "").toLowerCase();
  if (name.includes("legal")) return "legal";
  if (name.includes("trade")) return "trade";
  return "trade";
};

const getPrintedFieldStatus = (rows = [], matchers = []) => {
  const normalizedMatchers = matchers.map((m) => m.toLowerCase().replace(/\s+/g, ""));
  const item = rows.find((row) => {
    const name = String(row?.name || "").toLowerCase().replace(/\s+/g, "");
    return normalizedMatchers.some((matcher) => name === matcher || name.includes(matcher));
  });
  return item?.status;
};

const mapApiToForm = (data = {}) => {
  const companyNameOnDocument = parseJsonArray(data.company_name_on_document);
  const fieldsPrintedDocument = parseJsonArray(data.fields_printed_document);

  const showAddressFromFields = getPrintedFieldStatus(fieldsPrintedDocument, [
    "companyaddress",
    "address",
  ]);
  const showVat = getPrintedFieldStatus(fieldsPrintedDocument, ["vatnumber", "vat"]);
  const showLicense = getPrintedFieldStatus(fieldsPrintedDocument, [
    "licensenumber",
    "license",
  ]);

  return {
    legalName: data.company_legal_name ?? DEFAULT_FORM.legalName,
    tradeName: data.company_trade_name ?? DEFAULT_FORM.tradeName,
    legalForm: data.legal_form ?? DEFAULT_FORM.legalForm,
    registrationNumber: data.registration_number ?? DEFAULT_FORM.registrationNumber,
    vatNumber: data.vat_number ?? DEFAULT_FORM.vatNumber,
    vatCountry: (data.vat_country || DEFAULT_FORM.vatCountry).toUpperCase(),
    licenseNumber: data.company_license_number ?? DEFAULT_FORM.licenseNumber,
    street: data.street_name ?? DEFAULT_FORM.street,
    houseNumber: data.house_number ?? DEFAULT_FORM.houseNumber,
    postalCode: data.postal_code ?? DEFAULT_FORM.postalCode,
    city: data.city ?? DEFAULT_FORM.city,
    country: normalizeCountryCode(data.country || DEFAULT_FORM.country),
    email: data.company_email ?? DEFAULT_FORM.email,
    phone: data.company_phone ?? DEFAULT_FORM.phone,
    displayNameMode: getDocumentNameMode(companyNameOnDocument),
    showAddress:
      typeof data.display_address === "boolean"
        ? data.display_address
        : showAddressFromFields ?? DEFAULT_FORM.showAddress,
    showVat: typeof showVat === "boolean" ? showVat : DEFAULT_FORM.showVat,
    showLicense: typeof showLicense === "boolean" ? showLicense : DEFAULT_FORM.showLicense,
    effectiveFrom: data.effective_from_date ?? DEFAULT_FORM.effectiveFrom,
  };
};

const mapFormToApiPayload = (form) => ({
  company_legal_name: form.legalName,
  company_trade_name: form.tradeName,
  legal_form: form.legalForm,
  registration_number: form.registrationNumber,
  vat_number: form.vatNumber,
  vat_country: form.vatCountry,
  company_license_number: form.licenseNumber,
  street_name: form.street,
  house_number: form.houseNumber,
  postal_code: form.postalCode,
  city: form.city,
  country: COUNTRY_CODE_TO_NAME[form.country] || form.country,
  company_email: form.email,
  company_phone: form.phone,
  effective_from_date: form.effectiveFrom,
  display_address: !!form.showAddress,
  company_name_on_document: [
    { name: "Legal Name", value: form.legalName, status: form.displayNameMode === "legal" },
    { name: "Trade Name", value: form.tradeName, status: form.displayNameMode === "trade" },
  ],
  fields_printed_document: [
    {
      name: "CompanyAddress",
      description: "Street, city, postal code, country",
      status: !!form.showAddress,
    },
    {
      name: "VAT Number",
      description: "Printed on all sales invoices",
      status: !!form.showVat,
    },
    {
      name: "License Number",
      description: "Travel agency license reference",
      status: !!form.showLicense,
    },
  ],
});

export default function CompanySettings() {
  const { showToast } = useToast();
  const { data: companySettings, loading: loadingSettings, error: loadError, refetch } = useGetCompanySettings();
  const { execute: updateCompanySettings, loading: saveLoading } = useUpdateCompanySettings();
  const [activeTab,   setActiveTab]   = useState("legal");
  const [form,        setForm]        = useState(DEFAULT_FORM);
  const [initialForm, setInitialForm] = useState(DEFAULT_FORM);
  const [isDirty,     setIsDirty]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAudit,   setShowAudit]   = useState(false);

  useEffect(() => {
    if (!companySettings) return;
    const mappedForm = mapApiToForm(companySettings);
    setForm(mappedForm);
    setInitialForm(mappedForm);
    setIsDirty(false);
  }, [companySettings]);

  const previewName = useMemo(
    () =>
      form.displayNameMode === "trade"
        ? form.tradeName || "SUNSKY"
        : form.legalName || "SUNSKY Travel NV",
    [form.displayNameMode, form.tradeName, form.legalName]
  );

  const handleChange = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setIsDirty(true);
  };

  const handleReset = () => { setForm(initialForm); setIsDirty(false); };

  const handleSave = async () => {
    try {
      await updateCompanySettings(mapFormToApiPayload(form));
      await refetch();
      setIsDirty(false);
      setShowConfirm(false);
      showToast("Company settings saved successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save settings", "error");
    }
  };

  return (
    <div className={styles.page}>

      {/* ── Sticky Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <div className={styles.pageTitle}>Company Settings</div>
          <div className={styles.pageCrumb}>
            System Settings &rsaquo; Company Settings
          </div>
        </div>
        <div className={styles.pageHeaderRight}>
          {isDirty && (
            <button className={styles.btnGhost} onClick={handleReset}>
              Discard
            </button>
          )}
          <button className={styles.btnOutline} onClick={() => setShowAudit(true)}>
            📋 Audit Log
          </button>
          <button
            className={styles.btnPrimary}
            disabled={!isDirty || saveLoading || loadingSettings}
            onClick={() => setShowConfirm(true)}
          >
            {saveLoading ? <span className={styles.spinner} /> : null}
            {isDirty ? "Save Changes" : "✓ Up to date"}
          </button>
        </div>
      </div>

      {/* ── Warning banner ── */}
      <div className={styles.banner}>
        <span className={styles.bannerIcon}>⚠️</span>
        <span>
          Company settings affect all generated documents — invoices, vouchers
          and confirmations. Changes apply to{" "}
          <strong>future documents only</strong>. Every edit is audit-logged.
        </span>
      </div>

      {loadingSettings && (
        <div className={styles.banner}>
          <span className={styles.bannerIcon}>⏳</span>
          <span>Loading company settings...</span>
        </div>
      )}

      {loadError && (
        <div className={styles.banner}>
          <span className={styles.bannerIcon}>❌</span>
          <span>
            Failed to load company settings: {loadError}
          </span>
        </div>
      )}

      {/* ── Unsaved changes pill ── */}
      {isDirty && (
        <div className={styles.dirtyPill}>
          <span className={styles.dirtyDot} />
          Unsaved changes
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className={styles.tabBar}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            <span className={styles.tabIcon}>{t.icon}</span>
            {t.label}
            {activeTab === t.id && <span className={styles.tabUnderline} />}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className={styles.content}>
        {activeTab === "legal"     && <LegalTab     form={form} onChange={handleChange} />}
        {activeTab === "documents" && <DocumentsTab form={form} onChange={handleChange} previewName={previewName} />}
        {activeTab === "currency"  && <CurrencyTab  />}
        {activeTab === "audit"     && <AuditTab     form={form} onChange={handleChange} onOpenLog={() => setShowAudit(true)} />}
      </div>

      {/* ── Modals ── */}
      <ConfirmSaveModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSave}
        loading={saveLoading}
      />
      <AuditLogModal
        open={showAudit}
        onClose={() => setShowAudit(false)}
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TAB: Legal & Address
══════════════════════════════════════════════════════════════ */
function LegalTab({ form, onChange }) {
  return (
    <div className={styles.tabContent}>

      <Section
        icon="🏢"
        title="Legal Company Identity"
        subtitle="Official registration and tax information"
        badge="Mandatory"
        badgeColor="blue"
      >
        <div className={styles.grid2}>
          <Field label="Company Legal Name" required>
            <input className={styles.input} value={form.legalName}
              onChange={(e) => onChange("legalName", e.target.value)}
              placeholder="e.g. SUNSKY Travel NV" />
          </Field>
          <Field label="Company Trade Name" required>
            <input className={styles.input} value={form.tradeName}
              onChange={(e) => onChange("tradeName", e.target.value)}
              placeholder="e.g. SUNSKY" />
          </Field>
          <Field label="Legal Form" required>
            <select className={styles.select} value={form.legalForm}
              onChange={(e) => onChange("legalForm", e.target.value)}>
              <option value="">Select…</option>
              {["NV","BV","BVBA","SA","SRL","GmbH","Ltd"].map((f) => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Registration Number" required>
            <input className={styles.input} value={form.registrationNumber}
              onChange={(e) => onChange("registrationNumber", e.target.value)}
              placeholder="e.g. 0123.456.789" />
          </Field>
          <Field label="VAT Number" required>
            <input className={styles.input} value={form.vatNumber}
              onChange={(e) => onChange("vatNumber", e.target.value)}
              placeholder="e.g. BE0123456789" />
          </Field>
          <Field label="VAT Country" required>
            <select className={styles.select} value={form.vatCountry}
              onChange={(e) => onChange("vatCountry", e.target.value)}>
              <option value="">Select…</option>
              {[{c:"BE",n:"Belgium"},{c:"NL",n:"Netherlands"},{c:"DE",n:"Germany"},{c:"FR",n:"France"},{c:"LU",n:"Luxembourg"}]
                .map(({c,n}) => <option key={c} value={c}>{c} — {n}</option>)}
            </select>
          </Field>
          <Field label="Company License Number" required
            hint="Travel agency license issued by authority" span={2}>
            <input className={styles.input} value={form.licenseNumber}
              onChange={(e) => onChange("licenseNumber", e.target.value)}
              placeholder="e.g. LIC-2024-00123" />
          </Field>
        </div>
      </Section>

      <Section icon="📍" title="Registered Address" subtitle="Official company address used on documents" badge="Mandatory" badgeColor="blue">
        <div className={styles.grid3}>
          <Field label="Street Name" required span={2}>
            <input className={styles.input} value={form.street}
              onChange={(e) => onChange("street", e.target.value)}
              placeholder="e.g. Antwerpsesteenweg" />
          </Field>
          <Field label="House Number" required>
            <input className={styles.input} value={form.houseNumber}
              onChange={(e) => onChange("houseNumber", e.target.value)}
              placeholder="e.g. 45" />
          </Field>
          <Field label="Postal Code" required>
            <input className={styles.input} value={form.postalCode}
              onChange={(e) => onChange("postalCode", e.target.value)}
              placeholder="e.g. 2000" />
          </Field>
          <Field label="City" required>
            <input className={styles.input} value={form.city}
              onChange={(e) => onChange("city", e.target.value)}
              placeholder="e.g. Antwerp" />
          </Field>
          <Field label="Country" required>
            <select className={styles.select} value={form.country}
              onChange={(e) => onChange("country", e.target.value)}>
              <option value="">Select…</option>
              {[{c:"BE",n:"Belgium"},{c:"NL",n:"Netherlands"},{c:"DE",n:"Germany"},{c:"FR",n:"France"}]
                .map(({c,n}) => <option key={c} value={c}>{n}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section icon="📞" title="Contact Details" subtitle="Primary company contact information" badge="Mandatory" badgeColor="blue">
        <div className={styles.grid2}>
          <Field label="Company Email" required>
            <input className={styles.input} type="email" value={form.email}
              onChange={(e) => onChange("email", e.target.value)}
              placeholder="e.g. info@sunsky.be" />
          </Field>
          <Field label="Company Phone" required>
            <input className={styles.input} value={form.phone}
              onChange={(e) => onChange("phone", e.target.value)}
              placeholder="e.g. +32 3 123 45 67" />
          </Field>
        </div>
      </Section>

    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TAB: Document Display
══════════════════════════════════════════════════════════════ */
function DocumentsTab({ form, onChange, previewName }) {
  return (
    <div className={styles.tabContent}>
      <div className={styles.docLayout}>

        {/* Left: settings */}
        <div className={styles.docSettings}>
          <Section icon="📄" title="Document Display Settings"
            subtitle="Controls what appears on invoices, vouchers, and confirmations">

            <div className={styles.fieldBlock}>
              <div className={styles.blockLabel}>Company name shown on documents</div>
              <div className={styles.radioRow}>
                {["legal","trade"].map((mode) => (
                  <label key={mode}
                    className={`${styles.radioCard} ${form.displayNameMode === mode ? styles.radioCardActive : ""}`}>
                    <input type="radio" name="displayNameMode" value={mode}
                      checked={form.displayNameMode === mode}
                      onChange={() => onChange("displayNameMode", mode)}
                      className={styles.radioHidden} />
                    <div className={`${styles.radioCircle} ${form.displayNameMode === mode ? styles.radioCircleActive : ""}`} />
                    <div>
                      <div className={styles.radioTitle}>
                        {mode === "legal" ? "Legal Name" : "Trade Name"}
                      </div>
                      <div className={styles.radioValue}>
                        {mode === "legal" ? (form.legalName || "SUNSKY Travel NV") : (form.tradeName || "SUNSKY")}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.fieldBlock}>
              <div className={styles.blockLabel}>Fields printed on documents</div>
              {[
                { key: "showAddress", label: "Company Address",        desc: "Street, city, postal code, country" },
                { key: "showVat",     label: "VAT Number",             desc: "Printed on all sales invoices" },
                { key: "showLicense", label: "License Number",         desc: "Travel agency license reference" },
              ].map((item) => (
                <div className={styles.toggleRow} key={item.key}>
                  <div>
                    <div className={styles.toggleLabel}>{item.label}</div>
                    <div className={styles.toggleDesc}>{item.desc}</div>
                  </div>
                  <ToggleSwitch
                    checked={form[item.key]}
                    onChange={() => onChange(item.key, !form[item.key])}
                  />
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* Right: live preview */}
        <div className={styles.docPreviewCol}>
          <div className={styles.previewCard}>
            <div className={styles.previewCardHeader}>
              <span className={styles.previewCardIcon}>👁</span>
              Live Preview
            </div>
            <div className={styles.previewSheet}>
              <div className={styles.previewSheetHeader}>
                <div className={styles.previewLogo}>☀️</div>
                <div>
                  <div className={styles.previewCompany}>{previewName}</div>
                  {form.showAddress && (
                    <div className={styles.previewMeta}>
                      {form.street} {form.houseNumber}, {form.postalCode} {form.city}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.previewSheetDivider} />
              <div className={styles.previewFields}>
                {form.showVat && (
                  <div className={styles.previewField}>
                    <span className={styles.previewFieldKey}>VAT</span>
                    <span className={styles.previewFieldVal}>{form.vatNumber}</span>
                  </div>
                )}
                {form.showLicense && (
                  <div className={styles.previewField}>
                    <span className={styles.previewFieldKey}>License</span>
                    <span className={styles.previewFieldVal}>{form.licenseNumber}</span>
                  </div>
                )}
                <div className={styles.previewField}>
                  <span className={styles.previewFieldKey}>Email</span>
                  <span className={styles.previewFieldVal}>{form.email}</span>
                </div>
                <div className={styles.previewField}>
                  <span className={styles.previewFieldKey}>Phone</span>
                  <span className={styles.previewFieldVal}>{form.phone}</span>
                </div>
              </div>
              <div className={styles.previewInvoiceLabel}>INVOICE #INV-2025-0042</div>
            </div>
            <div className={styles.previewNote}>
              Updates live as you change settings
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TAB: Currency Format
══════════════════════════════════════════════════════════════ */
function CurrencyTab() {
  return (
    <div className={styles.tabContent}>
      <Section icon="💶" title="Number & Currency Formatting"
        subtitle="Display rules applied across all screens, reports, and documents"
        badge="System-Locked" badgeColor="gray">

        <div className={styles.lockedBanner}>
          🔒 These rules are system-defined and cannot be changed. They apply
          uniformly to all backend screens, frontend, reports, and documents.
        </div>

        <div className={styles.currencyGrid}>
          {[
            { label: "Currency Code",       value: "EUR",        icon: "💱" },
            { label: "Decimal Places",      value: "2",          icon: "🔢" },
            { label: "Thousand Separator",  value: ". (dot)",    icon: "📐" },
            { label: "Decimal Separator",   value: ", (comma)",  icon: "📐" },
            { label: "Currency Position",   value: "Before amount", icon: "↔️" },
          ].map((item) => (
            <div className={styles.currencyTile} key={item.label}>
              <div className={styles.currencyTileIcon}>{item.icon}</div>
              <div className={styles.currencyTileLabel}>{item.label}</div>
              <div className={styles.currencyTileValue}>{item.value}</div>
            </div>
          ))}
        </div>

        <div className={styles.divider} />
        <div className={styles.blockLabel}>Format examples</div>

        <div className={styles.examplesGrid}>
          {[
            { label: "Standard",        value: "EUR 1.900.101,00", type: "normal" },
            { label: "Negative amount", value: "EUR -1.234,56",    type: "negative" },
            { label: "Zero / null",     value: "EUR 0,00",         type: "zero" },
            { label: "Small amount",    value: "EUR 49,95",        type: "normal" },
          ].map((ex) => (
            <div className={styles.exampleTile} key={ex.label}>
              <span className={styles.exampleTileLabel}>{ex.label}</span>
              <span className={`${styles.exampleTileValue} ${ex.type === "negative" ? styles.exampleNeg : ""}`}>
                {ex.value}
              </span>
            </div>
          ))}
        </div>

        <p className={styles.currencyFootnote}>
          ⚠️ Rounding logic is out of scope for this configuration.
          Raw numeric API values remain unformatted. No locale or browser dependency applies.
        </p>
      </Section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TAB: Validity & Audit
══════════════════════════════════════════════════════════════ */
function AuditTab({ form, onChange, onOpenLog }) {
  return (
    <div className={styles.tabContent}>
      <Section icon="📅" title="Administrative Validity"
        subtitle="When these settings take effect on new documents">
        <div className={styles.validityBanner}>
          ℹ️ Changes to company settings only affect future documents.
          All historical documents remain unchanged.
        </div>
        <div className={styles.grid2} style={{ marginTop: 16 }}>
          <Field label="Effective From Date" required
            hint="Date from which these settings apply to newly generated documents">
            <input className={styles.input} type="date"
              value={form.effectiveFrom}
              onChange={(e) => onChange("effectiveFrom", e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section icon="📋" title="Audit Trail"
        subtitle="Full change history for compliance and traceability">
        <div className={styles.auditSummaryRow}>
          {[
            { key: "Created",      val: "2024-01-15 09:32" },
            { key: "Last updated", val: "2025-11-20 14:07" },
            { key: "Updated by",   val: "Super Admin" },
          ].map(({ key, val }) => (
            <div className={styles.auditSummaryItem} key={key}>
              <span className={styles.auditSummaryKey}>{key}</span>
              <span className={styles.auditSummaryVal}>{val}</span>
            </div>
          ))}
          <button className={styles.btnOutlineSmall} onClick={onOpenLog}>
            View full log →
          </button>
        </div>
      </Section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   Reusable: Section wrapper
══════════════════════════════════════════════════════════════ */
function Section({ icon, title, subtitle, badge, badgeColor, children }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHead}>
        <div className={styles.sectionHeadLeft}>
          <span className={styles.sectionIcon}>{icon}</span>
          <div>
            <div className={styles.sectionTitle}>{title}</div>
            {subtitle && <div className={styles.sectionSubtitle}>{subtitle}</div>}
          </div>
        </div>
        {badge && (
          <span className={`${styles.badge} ${badgeColor === "blue" ? styles.badgeBlue : styles.badgeGray}`}>
            {badge}
          </span>
        )}
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Field({ label, required, hint, span, children }) {
  return (
    <div className={styles.field} style={span ? { gridColumn: `span ${span}` } : undefined}>
      {label && (
        <label className={styles.fieldLabel}>
          {label}
          {required && <span className={styles.fieldRequired}>*</span>}
        </label>
      )}
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </div>
  );
}